// src/config.js

// ------------------------------
// URL helpers
// ------------------------------
const stripTrailingSlashes = (s) => (s || "").replace(/\/+$/, "");

const isAbsoluteUrl = (s) => /^https?:\/\//i.test(s || "");

// WEB_BASE_URL: where the React app is served from (frontend origin)
// Default: current browser origin (works great for localhost + production)
export const WEB_BASE_URL = stripTrailingSlashes(
  process.env.REACT_APP_WEB_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "")
);

// API_BASE_URL: where FastAPI lives (axios baseURL)
export const API_BASE_URL = stripTrailingSlashes(
  process.env.REACT_APP_API_BASE_URL || "https://ghrianghraffy.com"
);

// ASSET_BASE_URL: where /uploads (or CDN) lives
// Default: API_BASE_URL (today your uploads are served from the same origin)
export const ASSET_BASE_URL = stripTrailingSlashes(
  process.env.REACT_APP_ASSET_BASE_URL || API_BASE_URL
);

// ------------------------------
// Frontend routes (client-side)
// ------------------------------
export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  PARENT_SIGNUP: "/parent-signup",
  PRINCIPAL_DASHBOARD: "/principal-dashboard",
  STAFF_DASHBOARD: "/staff-dashboard",
  ADMIN: "/admin",
  GALLERY_UPLOAD: "/gallery-upload",
  VIEW_PHOTOS: "/view-photos",
  PROFILE: "/profile",
  COMPLETE_REGISTRATION: "/complete-registration",
  PRINCIPAL_REGISTRATION: "/principal-registration",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  ACTION: "/action",
  FAQ: "/faq",
  LEARN_MORE: "/learn-more",
};

// ------------------------------
// API endpoint paths (used by api.get/post)
// ------------------------------
export const API_PATHS = {
  PROFILE: "/auth/profile",
  UPLOAD: "/photos/upload",
  MY_PARENT_INVITE: "/auth/my-parent-invite",
  GENERATE_PARENT_INVITE: "/auth/generate-parent-invite",
  PARENT_ACCOUNTS: "/auth/parent-accounts",
  WARMUP: "/internal/warmup",
  RESEND_REGISTRATION: "/auth/resend-complete-registration",
  PHOTOS_TO_REVIEW: "/photos/photos-to-review",
  REVIEW_STATUS: "/photos/review/status",
  APPROVE_PHOTO: (id) => `/photos/${id}/approve`,
  REJECT_PHOTO: (id) => `/photos/${id}/reject`,
  VIEW_PHOTOS: "/photos/view-photos",
  HASHTAG_PHOTOS: "/photos/hashtag-photos",
  HASHTAGS_SEARCH: "/photos/hashtags",
  UPDATE_DISPLAY_NAME: "/auth/update-display-name",
  UPDATE_PHOTO_STORY: (id) => `/photos/${id}/update-story`,
  UPDATE_SCHOOL_HASHTAG: "/auth/update-school-hashtag",
  MARK_MOBILE_INSTALLED: "/auth/mark-mobile-installed",
  INSTAGRAM_LOGIN: "/auth/instagram-login",
  INSTAGRAM_QUOTA: "/auth/instagram/content-publishing-limit",
  CREATE_STAFF: "/auth/create-staff",
  STAFF_LIST: "/auth/staff-list",
  CREATE_STAFF_UPLOAD: "/auth/create-staff/upload",
  REVOKE_ACCESS: (email) => `/auth/revoke-access/${encodeURIComponent(email)}`,
  GET_API_KEY: "/auth/api-key",
  GENERATE_API_KEY: "/auth/generate-api-key",
  MY_PHOTOS: "/photos/view-photos",
  STRIPE_CHECKOUT_SESSION: "/auth/stripe/create-checkout-session",
  STRIPE_PORTAL_SESSION: "/auth/stripe/create-portal-session",
  SUBSCRIPTION_STATUS: "/auth/subscription-status",
  ADMIN_USERS: "/auth/users",
  PENDING_PRINCIPALS: "/auth/pending-principals",
  APPROVE_PRINCIPAL: (email) =>
    `/auth/approve-principal?principal_email=${encodeURIComponent(email)}`,
  REJECT_PRINCIPAL: (email) =>
    `/auth/reject-principal?principal_email=${encodeURIComponent(email)}`,
  CREATE_USER: "/auth/create-user",
  DELETE_USER_PREVIEW: (email) => `/auth/users/${encodeURIComponent(email)}`,
  DELETE_USER_CONFIRM: (email) => `/auth/users/${encodeURIComponent(email)}?confirm=true`,
  EXTEND_TRIAL: (email, days) =>
    `/auth/admin/extend-trial?principal_email=${encodeURIComponent(email)}&extra_days=${days}`,
  WIPE_TRIAL_SCHOOL: "/auth/admin/wipe-trial-school",
  COMPLETE_REGISTRATION: "/auth/complete-registration",
  FORGOT_PASSWORD: "/auth/forgot-password",
  RESET_PASSWORD: "/auth/reset-password",
  LOGIN: "/auth/login",
  PARENT_REGISTER: "/auth/register-parent",
  GET_SCHOOL_DATA: (rollNumber) => `/auth/get-school-data/${rollNumber}`,
  PRINCIPAL_REGISTRATION: "/auth/principal-registration",
  REFRESH: "/auth/refresh",
  UPDATE_SCHOOL_WEBSITE: "/auth/update-school-website",
  RERUN_SCHOOL_WEBSITE_ANALYSIS: "/auth/rerun-school-website-analysis",
};

// ------------------------------
// Mobile build link
// ------------------------------
export const MOBILE_APP_INSTALL_URL =
  process.env.REACT_APP_MOBILE_APP_INSTALL_URL ||
  "https://expo.dev/accounts/limmy/projects/ghrianghraffy/builds/619dedcf-cc1d-4484-9434-39fd410f0aa2";

// ------------------------------
// Stripe (frontend)
// ------------------------------
export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Preferred env name (what you want)
export const STRIPE_PRICE_ID =
  process.env.REACT_APP_STRIPE_PRICE_ID ||
  process.env.REACT_APP_DEFAULT_PRICE_ID || // legacy fallback
  "price_YourDefaultPriceIdHere";

// Backwards-compatible alias so existing imports keep working
export const DEFAULT_PRICE_ID = STRIPE_PRICE_ID;

// ------------------------------
// Helper functions
// ------------------------------
export const getParentSignupUrl = (code) =>
  `${WEB_BASE_URL}${ROUTES.PARENT_SIGNUP}?code=${encodeURIComponent(code || "")}`;

// Use for building API links if needed (rare — axios uses API_BASE_URL already)
export const getApiUrl = (path) => {
  if (!path) return API_BASE_URL;
  if (isAbsoluteUrl(path)) return path;
  return `${API_BASE_URL}/${String(path).replace(/^\//, "")}`;
};

// Use everywhere you render images/videos/thumbnails
export const getAssetUrl = (path) => {
  if (!path) return "";
  if (isAbsoluteUrl(path)) return path;
  return `${ASSET_BASE_URL}/${String(path).replace(/^\//, "")}`;
};